import { computed, type ComputedRef, type Ref } from 'vue'
import type { EditorCatalogBootstrapResponse, EditorCategoryTreeItem } from '../services/editorCatalogApi'

type MaybeReadonlyRef<T> = Ref<T> | ComputedRef<T>

type UseEditorCatalogFlowInput = {
  tree: MaybeReadonlyRef<{ items?: EditorCategoryTreeItem[] | null } | null>
  bootstrap: MaybeReadonlyRef<EditorCatalogBootstrapResponse | null>
  selectedRootId: MaybeReadonlyRef<number | null>
  selectedSubId: MaybeReadonlyRef<number | null>
  selectedProductId?: MaybeReadonlyRef<number | null>
  categoryFilter?: (item: any) => boolean
}

export function useEditorCatalogFlow(input: UseEditorCatalogFlowInput) {
  const treeItems = computed(() => Array.isArray(input.tree.value?.items) ? input.tree.value!.items : [])

  const subcategories = computed<any[]>(() => {
    const rootId = Number(input.selectedRootId.value || 0)
    if (!(rootId > 0)) return []
    const filter = input.categoryFilter ?? (() => true)
    return treeItems.value
      .filter((item: any) => Number(item?.parentId ?? 0) === rootId)
      .filter((item: any) => filter(item))
      .sort((a: any, b: any) => {
        const pos = Number(a?.position ?? 0) - Number(b?.position ?? 0)
        if (pos !== 0) return pos
        return String(a?.title || '').localeCompare(String(b?.title || ''), 'de')
      })
  })

  const hasSubcategories = computed(() => subcategories.value.length > 0)

  const selectedCategoryId = computed<number | null>(() =>
    input.selectedSubId.value ?? input.selectedRootId.value ?? null,
  )

  const products = computed<any[]>(() => {
    const categoryId = Number(selectedCategoryId.value || 0)
    if (!(categoryId > 0)) return []
    const ids = input.bootstrap.value?.productsByCategory?.[String(categoryId)] ?? []
    return ids
      .map((id: any) => input.bootstrap.value?.products?.[String(Number(id || 0))])
      .filter(Boolean)
      .sort((a: any, b: any) => {
        const pos = Number(a?.position ?? 0) - Number(b?.position ?? 0)
        if (pos !== 0) return pos
        return String(a?.titleShort || a?.title || '').localeCompare(String(b?.titleShort || b?.title || ''), 'de')
      })
  })

  const suggestedPrimaryStep = computed<'sub' | 'prod' | null>(() => {
    if (!Number(input.selectedRootId.value || 0)) return null
    if (hasSubcategories.value && !Number(input.selectedSubId.value || 0)) return 'sub'
    if (!Number(input.selectedProductId?.value || 0) && products.value.length > 0) return 'prod'
    return null
  })

  return {
    subcategories,
    hasSubcategories,
    selectedCategoryId,
    products,
    suggestedPrimaryStep,
  }
}
